import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/common/ui/button";
import { Checkbox } from "@/common/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/common/ui/dialog";
import { Label } from "@/common/ui/label";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";

import { updateMembershipRoles } from "../api/organization-api";
import type { Member, SessionMembershipRole } from "../types";

interface EditMemberRolesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: Member;
  availableRoles: SessionMembershipRole[];
  onSaved: () => void;
}

export function EditMemberRolesModal({
  open,
  onOpenChange,
  member,
  availableRoles,
  onSaved,
}: EditMemberRolesModalProps): React.ReactElement {
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(() =>
    member.role_assignments.map((r) => r.role_id)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedRoleIds(member.role_assignments.map((r) => r.role_id));
      setError("");
      setIsSubmitting(false);
    }
  }, [open, member]);

  const toggleRole = (roleId: string): void => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
    setError("");
  };

  const handleSubmit = async (): Promise<void> => {
    setError("");

    if (selectedRoleIds.length === 0) {
      setError("Please select at least one role");
      return;
    }

    setIsSubmitting(true);
    try {
      await updateMembershipRoles(member.id, selectedRoleIds);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setError("You don't have permission to change this member's roles.");
        } else if (err.status === 404) {
          setError("Member no longer exists. Refresh and try again.");
        } else {
          setError("Something went wrong. Please try again.");
        }
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    if (isSubmitting) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Roles</DialogTitle>
          <DialogDescription>
            Update roles for <strong>{member.name}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Label>Roles</Label>
          {availableRoles.map((role) => {
            const checked = selectedRoleIds.includes(role.role_id);
            return (
              <label
                key={role.role_id}
                htmlFor={`edit-role-${role.role_id}`}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
                  checked ? "border-primary bg-primary/5" : "hover:bg-accent"
                )}
              >
                <Checkbox
                  id={`edit-role-${role.role_id}`}
                  data-testid={`role-checkbox-${role.role_id}`}
                  checked={checked}
                  onCheckedChange={() => toggleRole(role.role_id)}
                  className="mt-0.5"
                  aria-label={`${role.role_name} role`}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">{role.role_name}</p>
                  <p className="text-xs text-muted-foreground">{role.role_key}</p>
                </div>
              </label>
            );
          })}
        </div>

        {error && (
          <div
            data-testid="edit-member-error"
            className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button data-testid="edit-member-save" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
